import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { formatDateTime } from '@/domains/survey';
import type { IAdminParticipantDetail, IAdminSalesforceTask } from '../../api/participant-contracts';
import { participantsApi } from '../../api/participants-api';
import { absent } from '../../components/account-facts';
import {
  LoadError,
  Pager,
  PastTheEnd,
  Section,
  TableSkeleton,
  useAdminData
} from '../../components/admin-page';
import { formatDay } from '../../components/days';
import { TableFrame } from '../../components/table-frame';
import {
  disclosureWords,
  revealDisclosure,
  type DisclosureLookup,
  type FindDisclosure
} from './disclosures';
import { useEmptyParts } from './empty-parts';
import { MemberPart } from './household';
import { useListPaging } from './client-page';

/**
 * The Salesforce tasks archived beside each member's disclosures (SR-216's R6) — survey-api's
 * `GET …/participants/{id}/salesforce-tasks`, the `Task` rows filed under the member's Salesforce
 * survey and team records, newest first, paged by survey-api. History, read-only: the ETL archived
 * them at the migration, and nothing here reopens one or links into Salesforce — no Salesforce
 * address is configured, and Salesforce is being switched off.
 *
 * **Always drawn**, unlike Disclosures and Team: whether a member has a task takes a read to
 * know, and the hub document says nothing about it. **Its own paged read per member**, below a
 * loaded hub — the route is per participant — with the activity's rules: three arms, a stale
 * banner over a page already drawn, `PastTheEnd` for a page past the last, and a pager drawn from
 * the page last read. **Read once, never re-read** — not on the poll, not on `written`: the
 * archive changes only when the ETL runs, and nothing on this page writes to it.
 *
 * **A task that names a disclosure is drawn by it** — its type and day, as the Disclosures
 * section draws them — and its press scrolls to that disclosure and opens it (`revealDisclosure`).
 * survey-api decided which disclosure a task names (`disclosureId`, from Comments that were one
 * Lightning record URL); the words come from what the Disclosures section drew (`find`), not a
 * second read of its route — the copy under the task's own member where two members' parts draw
 * it (a person and their team). A disclosure the page does not draw is said to be not on this
 * page, by its id — while a read that could hold it is still out, or failed, the page says only
 * its id, since it cannot know. **Comments are text, never a link**, a Lightning URL into another
 * org included.
 */
export function SalesforceTasksSection({
  members,
  find,
  anchor
}: {
  members: IAdminParticipantDetail[];
  /** Where a disclosure stands on this page — the Disclosures section's `useDrawnDisclosures`. */
  find: FindDisclosure;
  anchor: string;
}) {
  const multi = members.length > 1;
  const { empty, say } = useEmptyParts(members.map((member) => member.participantId));
  return (
    <Section
      title="Salesforce tasks"
      anchor={anchor}
      empty={empty}
      description={
        'Archived from Salesforce at the migration, and read-only: nothing here reopens a task. ' +
        'A task that named a disclosure leads to it under Disclosures.'
      }
    >
      <CardContent className="grid gap-8">
        {members.map((member, index) => (
          <MemberPart
            key={member.participantId}
            member={member}
            opened={index === 0}
            multi={multi}
          >
            <MemberTasks participantId={member.participantId} find={find} onCount={say} />
          </MemberPart>
        ))}
      </CardContent>
    </Section>
  );
}

function MemberTasks({
  participantId,
  find,
  onCount
}: {
  participantId: string;
  find: FindDisclosure;
  /** Says how many tasks the archive holds for the member, as a read lands. */
  onCount: (participantId: string, count: number) => void;
}) {
  const { page, pageSize, setPage, more } = useListPaging();
  const { data, error, reload } = useAdminData(
    (signal) =>
      participantsApi.listSalesforceTasks(participantId, { page, pageSize }, signal),
    [participantId, page, pageSize]
  );
  // The activity's rule: the pager is drawn from the page last read, so after a failed read of
  // page 2 its "Next" is for page 2 again — `page` already, which setting would not ask for.
  const turnTo = (target: number) => (target === page ? void reload() : setPage(target));
  const total = data?.total;
  React.useEffect(() => {
    if (total !== undefined) onCount(participantId, total);
  }, [participantId, total, onCount]);

  return (
    <div className="grid gap-4">
      {error &&
        (data === null ? (
          <LoadError message={error} />
        ) : (
          <Alert variant="destructive">
            <AlertTitle>
              Could not refresh the Salesforce tasks — showing them as last read
            </AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ))}
      {data === null ? (
        error ? null : <TableSkeleton rows={2} />
      ) : data.total === 0 ? (
        // An empty archive is one line, not a table's head over nothing (SR-407).
        <p className="text-sm">{absent('No Salesforce task is archived for this participant.')}</p>
      ) : (
        <>
          <TasksTable
            tasks={data.items}
            find={(disclosureId) => find(disclosureId, participantId)}
            // Only past the end: a total of none is the line above.
            empty={
              <PastTheEnd
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                counted={['task', 'tasks']}
                onPage={turnTo}
              />
            }
          />
          <Pager
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={turnTo}
            quiet
            onMore={more}
          />
        </>
      )}
    </div>
  );
}

function TasksTable({
  tasks,
  find,
  empty
}: {
  tasks: IAdminSalesforceTask[];
  find: (disclosureId: string) => DisclosureLookup;
  empty: React.ReactNode;
}) {
  return (
    <TableFrame>
      <Table aria-label="Salesforce tasks">
        <TableHeader>
          <TableRow>
            <TableHead>Created</TableHead>
            <TableHead>Subject</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Owner</TableHead>
            <TableHead>Created by</TableHead>
            <TableHead>Due</TableHead>
            <TableHead>Completed</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tasks.map((task) => (
            <TableRow key={task.sfId} data-sf-id={task.sfId} className="align-top">
              <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                {formatDateTime(task.createdAt)}
              </TableCell>
              <TableCell className="min-w-56">
                <div className="grid gap-1">
                  <span>{task.subject}</span>
                  {task.disclosureId !== null && (
                    <NamedDisclosure lookup={find(task.disclosureId)} id={task.disclosureId} />
                  )}
                  {task.description !== null && (
                    <span
                      data-description
                      className="text-sm whitespace-pre-line wrap-anywhere text-muted-foreground"
                    >
                      {task.description}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell>{task.status}</TableCell>
              <TableCell>{task.owner}</TableCell>
              <TableCell>{task.createdBy}</TableCell>
              <TableCell className="whitespace-nowrap">
                {task.dueDate !== null && formatDay(task.dueDate)}
              </TableCell>
              <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                {task.completedAt !== null && formatDateTime(task.completedAt)}
              </TableCell>
            </TableRow>
          ))}
          {tasks.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                {empty}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableFrame>
  );
}

/** The disclosure a task names: a press that scrolls to it where it is drawn; its id alone while
 *  a read that could hold it is out or failed; said to be elsewhere when it is not on this page. */
function NamedDisclosure({ lookup, id }: { lookup: DisclosureLookup; id: string }) {
  const code = <code className="text-xs select-all">{id}</code>;
  if (lookup.state === 'drawn') {
    return (
      <span>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0 text-left whitespace-normal"
          onClick={() => revealDisclosure(lookup.participantId, id)}
        >
          {disclosureWords(lookup.disclosure)}
        </Button>
      </span>
    );
  }
  return (
    <span data-disclosure-elsewhere className="text-sm text-muted-foreground">
      {lookup.state === 'absent' ? (
        <>A disclosure not on this page: {code}</>
      ) : (
        <>Disclosure {code}</>
      )}
    </span>
  );
}

